import { yup } from '@framework/validation';
import { v4 } from 'uuid';
import { BooleanConditionSchema } from '../../../../../inputs/expressions-editor/conditions/BooleanConditionSchema';
import { IBooleanCondition } from '../../../../../inputs/expressions-editor/conditions/IBooleanCondition';
import { IAdvancedRichText } from '../../../../../inputs/rich-text-advanced/IAdvancedRichText';
import { RichTextAdvancedSchema } from '../../../../../inputs/rich-text-advanced/schema/RichTextAdvancedSchema';
import { widgetCodeSchema } from '../../../validation/widgetCodeSchema';
import { ITableCell, TableCellSchema } from './ITableCell';

export enum TableRowType {
  Simple = 'simple',
  Custom = 'custom',
}

/**
 * Rappresenta una riga della tabella "standard", ovvero senza campi personalizzati
 */
export interface ISimpleTableRow {
  id: string;
  customRow?: false;
  code?: string;
  label?: IAdvancedRichText;
  visible?: IBooleanCondition;
  enabled?: IBooleanCondition;
}

/**
 * Rappresenta una riga della tabella con campi personalizzati, ovvero le cui
 * celle sono dei widget diversi da quelli definiti nelle colonne della
 * tabella.
 */
export interface ICustomTableRow {
  id: string;
  customRow: true;
  code?: string;
  label?: IAdvancedRichText;
  cells: ITableCell[];
  visible?: IBooleanCondition;
  enabled?: IBooleanCondition;
}

export function isCustomRow(row: ITableRow): row is ICustomTableRow {
  return row.customRow === true;
}

export type ITableRow = ISimpleTableRow | ICustomTableRow;
export type ITableRowErrorable = ITableRow & { isError?: boolean };

const SimpleTableRowSchema: yup.ObjectSchema<ISimpleTableRow> = yup.object({
  id: yup
    .string()
    .uuid()
    .required()
    .label('ID')
    .default(() => v4()),
  customRow: yup
    .boolean<false>()
    .optional()
    .default(false)
    .label('Riga personalizzata'),
  code: widgetCodeSchema,
  label: RichTextAdvancedSchema.label('Etichetta'),
  visible: BooleanConditionSchema,
  enabled: BooleanConditionSchema,
});

const CustomTableRowSchema: yup.ObjectSchema<ICustomTableRow> = yup.object({
  id: yup
    .string()
    .uuid()
    .required()
    .label('ID')
    .default(() => v4()),
  customRow: yup
    .boolean<true>()
    .required()
    .default(true)
    .label('Riga personalizzata'),
  code: widgetCodeSchema,
  label: RichTextAdvancedSchema.label('Etichetta'),
  cells: yup.array(TableCellSchema).required(),
  visible: BooleanConditionSchema,
  enabled: BooleanConditionSchema,
});

export const TableRowSchema = yup.lazy(value => {
  if (!value?.customRow) {
    return SimpleTableRowSchema;
  }
  return CustomTableRowSchema;
});
